import { createContext, ReactNode, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { immer } from "zustand/middleware/immer"
import { ChatThread } from "@/modules/chats/types/chat-thread"

interface ChatThreadState {
  selectedThread?: ChatThread
  setSelectedThread: (chatThread: ChatThread | undefined) => void
}

const createChatThreadStore = () => {
  return create<ChatThreadState>()(
    immer((set) => ({
      selectedThread: undefined,
      setSelectedThread: (selectedThread) => {
        set({ selectedThread })
      },
    })),
  )
}

type ChatThreadStore = ReturnType<typeof createChatThreadStore>

const ChatThreadStoreContext = createContext<ChatThreadStore | null>(null)

const emptyChatThreadStore = createChatThreadStore()

const ChatThreadProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<ChatThreadStore>(createChatThreadStore())

  return <ChatThreadStoreContext.Provider value={storeRef.current}>{children}</ChatThreadStoreContext.Provider>
}

function useChatThreadContext<T>(selector: (state: ChatThreadState) => T): T {
  const store = useContext(ChatThreadStoreContext)
  if (!store) throw new Error("Missing ChatThreadStoreContext.Provider in the tree")

  return useStore(store, selector)
}

function useChatThreadStore(): ChatThreadStore {
  const store = useContext(ChatThreadStoreContext)
  if (!store) throw new Error("Missing ChatThreadStoreContext.Provider in the tree")

  return store
}

function useOptionalChatThreadContext<T>(selector: (state: ChatThreadState) => T): T {
  const store = useContext(ChatThreadStoreContext)

  return useStore(store ?? emptyChatThreadStore, selector)
}

export {
  ChatThreadProvider,
  createChatThreadStore,
  useChatThreadContext,
  useChatThreadStore,
  useOptionalChatThreadContext,
}
